import { useEffect, useMemo, useState } from 'react';
import { BlogApi } from '../api/adminApi';
import { fallbackBlogs, shapeBlog } from './blogHelpers';

let cachedBlogs = null;
let cachedIsFallback = false;
let inflightRequest = null;

const fetchBlogs = async () => {
  if (cachedBlogs) {
    return { data: cachedBlogs, isFallback: cachedIsFallback };
  }
  if (inflightRequest) {
    return inflightRequest;
  }
  inflightRequest = BlogApi.getAll()
    .then((data) => {
      if (Array.isArray(data) && data.length) {
        cachedBlogs = data.map(shapeBlog);
        cachedIsFallback = false;
      } else {
        cachedBlogs = fallbackBlogs;
        cachedIsFallback = true;
      }
      return { data: cachedBlogs, isFallback: cachedIsFallback };
    })
    .catch(() => {
      cachedBlogs = fallbackBlogs;
      cachedIsFallback = true;
      return { data: cachedBlogs, isFallback: cachedIsFallback };
    })
    .finally(() => {
      inflightRequest = null;
    });

  return inflightRequest;
};

export const useBlogs = ({ topicSlug, search } = {}) => {
  const [blogs, setBlogs] = useState(cachedBlogs || fallbackBlogs);
  const [isLoading, setIsLoading] = useState(!cachedBlogs);
  const [error, setError] = useState('');
  const [isFallback, setIsFallback] = useState(cachedIsFallback);

  useEffect(() => {
    let isMounted = true;

    if (cachedBlogs) {
      setIsLoading(false);
      return () => {
        isMounted = false;
      };
    }

    setError('');
    fetchBlogs()
      .then(({ data, isFallback: fallbackFlag }) => {
        if (isMounted) {
          setBlogs(data);
          setIsFallback(fallbackFlag);
        }
      })
      .catch(() => {
        if (isMounted) {
          setError('Unable to load latest blog posts. Showing defaults.');
          setBlogs(fallbackBlogs);
          setIsFallback(true);
        }
      })
      .finally(() => {
        if (isMounted) {
          setIsLoading(false);
        }
      });

    return () => {
      isMounted = false;
    };
  }, []);

  const normalizedTopic = typeof topicSlug === 'string' ? topicSlug.trim().toLowerCase() : '';
  const normalizedSearch = typeof search === 'string' ? search.trim().toLowerCase() : '';

  const filteredBlogs = useMemo(() => {
    let next = blogs;
    if (normalizedTopic) {
      next = next.filter((blog) => (blog.topics || []).some((topic) => topic.slug === normalizedTopic));
    }
    if (normalizedSearch) {
      next = next.filter((blog) => {
        const haystack = `${blog.title} ${blog.excerpt} ${blog.author} ${blog.tags.join(' ')}`.toLowerCase();
        return haystack.includes(normalizedSearch);
      });
    }
    return next;
  }, [blogs, normalizedTopic, normalizedSearch]);

  return {
    blogs: filteredBlogs,
    allBlogs: blogs,
    isLoading,
    error,
    isFallback
  };
};

export const getCachedBlogById = (id) => {
  if (!id || !cachedBlogs) return null;
  return cachedBlogs.find((blog) => String(blog.id) === String(id)) || null;
};

export const getCachedBlogBySlug = (slug) => {
  if (!slug || !cachedBlogs) return null;
  const normalized = slug.trim().toLowerCase();
  return cachedBlogs.find((blog) => blog.slug === normalized) || null;
};

export const primeBlogCache = (items) => {
  if (!Array.isArray(items) || !items.length) return;
  cachedBlogs = items.map(shapeBlog);
  cachedIsFallback = false;
};
